import { DataError, type DataSource, type EntityKey, type Entities, type ListQuery, type Page } from './source';

/** Bỏ dấu tiếng Việt, chữ thường. */
export const fold = (s: string): string =>
  s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/đ/g, 'd');

type Rows = { [K in EntityKey]?: readonly Entities[K][] };

export interface FixtureOptions {
  /** khoá chính của từng thực thể (mặc định 'id') */
  idField?: Partial<Record<EntityKey, string>>;
  /** giả lập trễ mạng (ms) để thấy trạng thái loading */
  delay?: number;
}

const field = (row: unknown, f: string): unknown => (row as Record<string, unknown>)[f];

function matches(row: unknown, q: ListQuery): boolean {
  if (q.where) {
    for (const [k, v] of Object.entries(q.where)) if (field(row, k) !== v) return false;
  }
  if (q.q?.trim()) {
    const n = fold(q.q.trim());
    const hay = Object.values(row as Record<string, unknown>)
      .filter((v) => typeof v === 'string' || typeof v === 'number')
      .map((v) => fold(String(v)))
      .join(' ');
    if (!hay.includes(n)) return false;
  }
  return true;
}

/** Nhà cung cấp dữ liệu mẫu: vùng nạp bảng ghi của board (`register`), màn đọc qua DataSource. */
export function fixtureSource(initial: Rows = {}, opt: FixtureOptions = {}): DataSource & { register<K extends EntityKey>(entity: K, rows: readonly Entities[K][]): void } {
  const rows: Rows = { ...initial };
  const wait = () => (opt.delay ? new Promise((r) => setTimeout(r, opt.delay)) : Promise.resolve());
  const all = <K extends EntityKey>(e: K): readonly Entities[K][] => (rows[e] as readonly Entities[K][] | undefined) ?? [];
  return {
    register(entity, list) {
      (rows as Record<string, unknown>)[entity] = list;
    },
    async list<K extends EntityKey>(entity: K, query: ListQuery = {}): Promise<Page<Entities[K]>> {
      await wait();
      let out = all(entity).filter((r) => matches(r, query));
      if (query.sort) {
        const { field: f, dir = 'asc' } = query.sort;
        const k = dir === 'asc' ? 1 : -1;
        out = [...out].sort((a, b) => {
          const x = field(a, f);
          const y = field(b, f);
          if (typeof x === 'number' && typeof y === 'number') return (x - y) * k;
          return String(x ?? '').localeCompare(String(y ?? ''), 'vi') * k;
        });
      }
      const total = out.length;
      const off = query.offset ?? 0;
      return { items: out.slice(off, query.limit === undefined ? undefined : off + query.limit), total };
    },
    async get<K extends EntityKey>(entity: K, id: string): Promise<Entities[K] | null> {
      await wait();
      const f = opt.idField?.[entity] ?? 'id';
      return all(entity).find((r) => String(field(r, f)) === id) ?? null;
    },
  };
}

export { DataError };
